import React, { ReactNode, useEffect, useState } from "react";
import { arrayHasAtLeastOneItem } from "common/utils/arrays";

import { Body, Title } from "./Typography";
import { Button } from "./Button";
import { IconButton } from "./Icons/LucideIcons";
import { handleKeyDown } from "../../utils/accessibility";
import { spacing } from "../../stitches.config";

import { FlexColumn, FlexRow } from "./__styles__/Layout";
import {
  ButtonSection,
  ContentSection,
  FormSection,
  HeaderSection,
  PrimaryButtons,
  SecondaryButtons,
  UpdatedContainer as Container,
} from "./__styles__/Modal";

type MultiStepContent = (props: {
  handleNext?: () => void;
  handlePrevious?: () => void;
}) => ReactNode;

export type CommonModalComponentProps = {
  title: string;
  subtitle?: string;
  height?: "full" | "short";
  size?: "small" | "medium" | "large";
  content: ReactNode | MultiStepContent;
  onSubmit?: () => void;
  onCancel: () => void;
  loading?: boolean;
  disabled?: boolean;
  primaryButtonText?: string;
  autoFocusCancelButton?: boolean;
  buttonLinks?: { text: string; onClick: () => void }[];
  variant?: "primary" | "destructive";
  dataTestId?: string;
  secondaryButtons?: { text: string; onClick: () => void }[];
  overflows?: boolean;
  showNextButton?: boolean;
};

export type ModalStep = Pick<
  CommonModalComponentProps,
  | "title"
  | "subtitle"
  | "content"
  | "loading"
  | "disabled"
  | "primaryButtonText"
  | "overflows"
>;

const ModalContent = ({
  title,
  subtitle,
  content,
  onSubmit,
  onCancel,
  loading = false,
  disabled,
  primaryButtonText,
  autoFocusCancelButton,
  variant = "primary",
  secondaryButtons,
  handlePrevious,
  handleNext,
  overflows = true,
  showNextButton,
}: Omit<CommonModalComponentProps, "onSubmit"> & {
  handlePrevious?: () => void;
  handleNext?: () => void;
  onSubmit?: () => void;
  showNextButton?: boolean;
}) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCancel();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const isStepModal = handlePrevious || handleNext;

  const showButtonSection =
    onSubmit || arrayHasAtLeastOneItem(secondaryButtons) || showNextButton;

  content =
    isStepModal && content instanceof Function
      ? content({ handleNext, handlePrevious })
      : content;

  return (
    <>
      <HeaderSection>
        <FlexRow style={{ gap: `${spacing.s}`, paddingRight: `${spacing.xl}` }}>
          {handlePrevious && (
            <IconButton
              testId="backButton"
              iconName={"chevron-left"}
              onClick={handlePrevious}
            />
          )}
          <FlexColumn style={{ gap: `${spacing["2xs"]}` }}>
            <Title size="small" type="semiBold">
              {title}
            </Title>
            {subtitle && (
              <Body type="regular" size="large">
                {subtitle}
              </Body>
            )}
          </FlexColumn>
        </FlexRow>
      </HeaderSection>
      <FormSection overflows={overflows} tabIndex={1}>
        <ContentSection overflows={overflows} css={{ gap: spacing.m }}>
          {content}
        </ContentSection>
        {showButtonSection && (
          <ButtonSection>
            {arrayHasAtLeastOneItem(secondaryButtons) && (
              <SecondaryButtons>
                {secondaryButtons.map(({ text, onClick }) => (
                  <Button
                    key={text}
                    styleVariant="buttonLink"
                    onClick={event => {
                      event.preventDefault();
                      onClick();
                    }}
                    size="medium"
                  >
                    {text}
                  </Button>
                ))}
              </SecondaryButtons>
            )}
            <PrimaryButtons>
              <Button
                styleVariant="secondary"
                onClick={event => {
                  event.preventDefault();
                  onCancel();
                }}
                disabled={loading}
                size="medium"
                onKeyDown={event => {
                  handleKeyDown({ event, keyDownFn: onCancel });
                }}
                autoFocus={autoFocusCancelButton}
              >
                Cancel
              </Button>
              {showNextButton && handleNext && (
                <Button
                  styleVariant="primary"
                  onClick={e => {
                    e.preventDefault();
                    handleNext();
                  }}
                  loading={loading}
                  disabled={disabled || loading}
                  size="medium"
                  onKeyDown={event => {
                    handleKeyDown({ event, keyDownFn: handleNext });
                  }}
                >
                  Next
                </Button>
              )}
              {onSubmit && (
                <Button
                  styleVariant={variant === "destructive" ? "alert" : "primary"}
                  onClick={e => {
                    e.preventDefault();
                    onSubmit();
                  }}
                  loading={loading}
                  disabled={disabled || loading}
                  size="medium"
                  onKeyDown={event => {
                    handleKeyDown({ event, keyDownFn: onSubmit });
                  }}
                  type="button"
                >
                  {primaryButtonText || "Save"}
                </Button>
              )}
            </PrimaryButtons>
          </ButtonSection>
        )}
      </FormSection>
    </>
  );
};

export const CommonModal = ({
  height,
  size = "medium",
  onCancel,
  dataTestId,
  overflows = true,
  ...props
}: CommonModalComponentProps & {
  handlePrevious?: () => void;
}) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCancel();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <Container
      size={size}
      data-testid={dataTestId}
      overflows={overflows}
      height={height}
    >
      <ModalContent onCancel={onCancel} overflows={overflows} {...props} />
    </Container>
  );
};

export const MultiStepModal = ({
  steps,
  size,
  height,
  onSubmit,
  onCancel,
  loading,
  dataTestId,
  showNextButton = false,
}: Pick<
  CommonModalComponentProps,
  "size" | "onSubmit" | "onCancel" | "dataTestId" | "height" | "loading"
> & {
  steps: Array<ModalStep>;
  showNextButton?: boolean;
}) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCancel();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const renderStep = (index: number) => {
    if (steps[index]) {
      switch (index) {
        //first step
        case 0:
          return (
            <ModalContent
              {...steps[index]}
              handleNext={handleNext}
              onCancel={onCancel}
              showNextButton={showNextButton}
              loading={loading}
            />
          );

        //last step
        case steps.length - 1:
          return (
            <ModalContent
              {...steps[index]}
              handlePrevious={handlePrevious}
              onCancel={onCancel}
              loading={loading}
              onSubmit={onSubmit}
            />
          );

        //middle steps
        default:
          return (
            <ModalContent
              {...steps[index]}
              handleNext={handleNext}
              handlePrevious={handlePrevious}
              onCancel={onCancel}
              showNextButton={showNextButton}
              loading={loading}
            />
          );
      }
    }
    return null;
  };

  return (
    <Container
      size={size}
      data-testid={dataTestId}
      height={height}
      overflows={steps[currentStepIndex]?.overflows}
    >
      {renderStep(currentStepIndex)}
    </Container>
  );
};
